import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@material-ui/core';
import {
  Send,
  AttachFile,
  MoreVert,
  Edit,
  Delete,
  People,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useChatroomSocket } from '../util/useChatroomSocket';
import {
  getDMMessagesCommunityReach,
  sendDMMessageCommunityReach,
  editDMMessageCommunityReach,
  deleteDMMessageCommunityReach,
  getChatroomMembersApi,
} from '../api';
import toastr from 'toastr';

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    padding: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
  },
  messageList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  message: {
    display: 'flex',
    gap: theme.spacing(1),
    '&.own-message': {
      flexDirection: 'row-reverse',
    },
  },
  messageContent: {
    maxWidth: '70%',
    padding: theme.spacing(1.5),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.background.paper,
    '&.own-message': {
      backgroundColor: '#E3F2FD', // Light blue
      color: '#1976D2', // Dark blue text
    },
  },
  messageText: {
    wordBreak: 'break-word',
  },
  messageTime: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
  },
  messageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(0.5),
  },
  senderName: {
    fontWeight: 600,
    fontSize: '0.875rem',
  },
  headerTime: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  ownMessageWrapper: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  otherMessageWrapper: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
  },
  smallAvatar: {
    width: 32,
    height: 32,
    fontSize: '0.875rem',
  },
  inputContainer: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  typingIndicator: {
    padding: theme.spacing(1, 2),
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
  },
  editingBanner: {
    padding: theme.spacing(1, 2),
    backgroundColor: theme.palette.info.light,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
}));

interface ChatWindowProps {
  roomId: string;
  roomName: string;
  userId: string;
  userName: string;
}

export default function ChatWindow({
  roomId,
  roomName,
  userId,
  userName,
}: ChatWindowProps) {
  const classes = useStyles();

  const [messages, setMessages] = useState<any[]>([]);
  const [inputValue, setInputValue] = useState(() => {
    try {
      return localStorage.getItem(`cr_draft_${roomId}`) || '';
    } catch {
      return '';
    }
  });
  const [loading, setLoading] = useState(false);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [editingMessage, setEditingMessage] = useState<any | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedMessage, setSelectedMessage] = useState<any | null>(null);
  const [membersDialogOpen, setMembersDialogOpen] = useState(false);
  const [members, setMembers] = useState<any[]>([]);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Socket integration
  const {
    joinChatroom,
    leaveChatroom,
    startTyping,
    stopTyping,
    markAsRead,
  } = useChatroomSocket({
    userId,
    onNewMessage: (data) => {
      if (data.roomId === roomId) {
        setMessages((prev) => [...prev, data.message]);
        scrollToBottom();
      }
    },
    onMessageEdited: (data) => {
      if (data.roomId === roomId) {
        setMessages((prev) =>
          prev.map((msg) => (msg._id === data.messageId ? data.message : msg))
        );
      }
    },
    onMessageRemoved: (data) => {
      if (data.roomId === roomId) {
        setMessages((prev) =>
          prev.map((msg) =>
            msg._id === data.messageId ? { ...msg, isRemoved: true } : msg
          )
        );
      }
    },
    onUserTyping: (data) => {
      if (data.roomId === roomId && data.userId !== userId) {
        if (data.isTyping) {
          setTypingUsers((prev) =>
            prev.includes(data.userId) ? prev : [...prev, data.userId]
          );
        } else {
          setTypingUsers((prev) => prev.filter((id) => id !== data.userId));
        }
      }
    },
  });

  // Load messages on mount
  useEffect(() => {
    loadMessages();
    joinChatroom(roomId);

    return () => {
      leaveChatroom(roomId);
    };
  }, [roomId, joinChatroom, leaveChatroom]);

  // Reload draft when roomId changes (ChatWindow reused without remounting)
  useEffect(() => {
    try {
      setInputValue(localStorage.getItem(`cr_draft_${roomId}`) || '');
    } catch {
      setInputValue('');
    }
  }, [roomId]);

  // Mark as read when messages change
  useEffect(() => {
    if (messages.length > 0) {
      markAsRead(roomId);
    }
  }, [messages.length, roomId, markAsRead]);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const result = await getDMMessagesCommunityReach({ room_id: roomId });
      const messages = result.data?.messages || [];

      // Transform messages to ensure senderName is populated
      const transformedMessages = messages.map((msg: any) => ({
        ...msg,
        senderName: msg.senderId?.name || msg.senderName || 'Unknown',
        senderId: typeof msg.senderId === 'object' ? msg.senderId?._id : msg.senderId,
      }));

      setMessages(transformedMessages);
      scrollToBottom();
    } catch (error) {
      console.error('Error loading messages:', error);
      toastr.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);

    // Persist draft to localStorage
    try {
      if (val) {
        localStorage.setItem(`cr_draft_${roomId}`, val);
      } else {
        localStorage.removeItem(`cr_draft_${roomId}`);
      }
    } catch {}

    // Start typing indicator
    startTyping(roomId);

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Stop typing after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      stopTyping(roomId);
    }, 2000);
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    try {
      if (editingMessage) {
        // Edit existing message
        await editDMMessageCommunityReach({
          messageId: editingMessage._id,
          message_text: inputValue,
          room_id: roomId,
          senderId: userId,
        } as any);
        setEditingMessage(null);
        toastr.success('Message edited');
      } else {
        // Send new message
        await sendDMMessageCommunityReach({
          message_text: inputValue,
          room_id: roomId,
          senderId: userId,
          message_type: 'text',
        } as any);
      }

      setInputValue('');
      try { localStorage.removeItem(`cr_draft_${roomId}`); } catch {}
      stopTyping(roomId);
      loadMessages(); // Reload to get the new message
    } catch (error) {
      console.error('Error sending message:', error);
      toastr.error('Failed to send message');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleMessageMenu = (event: React.MouseEvent<HTMLElement>, message: any) => {
    setAnchorEl(event.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedMessage(null);
  };

  const handleEditMessage = () => {
    if (selectedMessage) {
      setEditingMessage(selectedMessage);
      setInputValue(selectedMessage.message_text);
    }
    handleCloseMenu();
  };

  const handleDeleteMessage = async () => {
    if (!selectedMessage) return;

    try {
      await deleteDMMessageCommunityReach(selectedMessage._id);
      loadMessages();
      toastr.success('Message deleted');
    } catch (error) {
      console.error('Error deleting message:', error);
      toastr.error('Failed to delete message');
    }
    handleCloseMenu();
  };

  const handleCancelEdit = () => {
    setEditingMessage(null);
    setInputValue('');
    try { localStorage.removeItem(`cr_draft_${roomId}`); } catch {}
  };

  const handleOpenMembers = async () => {
    try {
      const result = await getChatroomMembersApi(roomId);
      setMembers(result.data || []);
      setMembersDialogOpen(true);
    } catch (error) {
      console.error('Error loading members:', error);
      toastr.error('Failed to load members');
    }
  };

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <Paper className={classes.root} elevation={3}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h6">{roomName}</Typography>
        <IconButton onClick={handleOpenMembers}>
          <People />
        </IconButton>
      </Box>

      {/* Messages */}
      <Box className={classes.messagesContainer}>
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Box className={classes.messageList}>
            {messages.map((message) => {
              const isOwnMessage = message.senderId === userId;
              const isRemoved = message.isRemoved;

              // Google Chat style: Own messages right-aligned without avatar
              // Others' messages left-aligned with avatar and name header
              if (isOwnMessage) {
                return (
                  <Box key={message._id} className={classes.ownMessageWrapper}>
                    <Box
                      className={`${classes.messageContent} own-message`}
                      style={{ position: 'relative' }}
                    >
                      <Typography className={classes.messageText}>
                        {isRemoved ? (
                          <em>This message has been removed</em>
                        ) : (
                          message.message_text
                        )}
                      </Typography>
                      <Box
                        display="flex"
                        justifyContent="flex-end"
                        alignItems="center"
                        style={{ gap: 4 }}
                      >
                        <Typography className={classes.messageTime}>
                          {formatTime(message.createdAt)}
                          {message.isEdited && ' (edited)'}
                        </Typography>
                        {!isRemoved && (
                          <IconButton
                            size="small"
                            onClick={(e) => handleMessageMenu(e, message)}
                            style={{ padding: 2 }}
                          >
                            <MoreVert fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>
                  </Box>
                );
              }

              // Other user's message - Google Chat style with avatar and header
              return (
                <Box key={message._id} className={classes.otherMessageWrapper}>
                  <Avatar className={classes.smallAvatar}>
                    {message.senderName?.charAt(0) || 'U'}
                  </Avatar>
                  <Box>
                    <Box className={classes.messageHeader}>
                      <Typography className={classes.senderName}>
                        {message.senderName || 'Unknown'}
                      </Typography>
                      <Typography className={classes.headerTime}>
                        {formatTime(message.createdAt)}
                      </Typography>
                    </Box>
                    <Box className={classes.messageContent}>
                      <Typography className={classes.messageText}>
                        {isRemoved ? (
                          <em>This message has been removed</em>
                        ) : (
                          message.message_text
                        )}
                      </Typography>
                      {message.isEdited && (
                        <Typography className={classes.messageTime}>
                          (edited)
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              );
            })}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

      {/* Typing indicator */}
      {typingUsers.length > 0 && (
        <Typography className={classes.typingIndicator}>
          {typingUsers.length === 1
            ? 'Someone is typing...'
            : `${typingUsers.length} people are typing...`}
        </Typography>
      )}

      {/* Editing banner */}
      {editingMessage && (
        <Box className={classes.editingBanner}>
          <Typography>Editing message</Typography>
          <Button size="small" onClick={handleCancelEdit}>
            Cancel
          </Button>
        </Box>
      )}

      {/* Input */}
      <Box className={classes.inputContainer}>
        <IconButton>
          <AttachFile />
        </IconButton>
        <TextField
          fullWidth
          placeholder="Type a message..."
          value={inputValue}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          variant="outlined"
          size="small"
        />
        <IconButton color="primary" onClick={handleSendMessage}>
          <Send />
        </IconButton>
      </Box>

      {/* Message menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem onClick={handleEditMessage}>
          <Edit fontSize="small" style={{ marginRight: 8 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDeleteMessage}>
          <Delete fontSize="small" style={{ marginRight: 8 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Members Dialog */}
      <Dialog
        open={membersDialogOpen}
        onClose={() => setMembersDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Chatroom Members</DialogTitle>
        <DialogContent>
          <List>
            {members.map((member) => (
              <ListItem key={member._id}>
                <ListItemAvatar>
                  <Avatar>
                    {member.room_user_id?.name?.charAt(0) || 'U'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={member.room_user_id?.name || 'Unknown'}
                  secondary={member.enrollment_role || 'member'}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMembersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}
